import { entityService } from '@/services/EntityService';
import { httpClient } from '@/utils/httpClient';
import { outputReport, getOutputFormat } from '@/utils/reportOutput';
import { getPersonLabels } from '@/hooks/usePersonLabels';
import { isUuid } from '@equip/utils';
import {
  buildCostOfBusinessReportHtml,
  type CostOfBusinessItem,
  type CostOfBusinessTemplateOptions,
} from '@/reports/templates/costOfBusinessTemplate';

interface TransactionLogAsset {
  assetId: string;
  lin: string;
  nsn: string;
  nomenclature: string;
  transQty: number;
  actualPrice?: number | null;
  tax?: number | null;
  shipping?: number | null;
  documentNumber?: string | null;
}

interface TransactionLogDetail {
  id: string;
  transactionType: string;
  memberName: string;
  memberId: string;
  dateProcessed: string;
  documentNumber?: string;
  assets: TransactionLogAsset[];
}

interface TransactionLogResponse {
  transactions: TransactionLogDetail[];
}

interface RawIeSummaryRow {
  person_id: string;
  lin: string | null;
  authorized_qty: number | null;
}

interface PersonProfile {
  organization: string | null;
  billet: string | null;
}

const chunk = <T>(values: T[], size: number): T[][] => {
  if (size <= 0) return [values];
  const chunks: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
};

const coerceString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeDateOnly = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const parseNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number.parseFloat(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const parseCsvList = (value: unknown): string[] => {
  if (typeof value !== 'string') return [];
  return value
    .split(',')
    .map((token) => token.trim().toUpperCase())
    .filter((token) => token.length > 0);
};

const toTitle = (value: string): string =>
  value
    .toLowerCase()
    .split(/[_\s]+/g)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

const formatTransactionTypeLabel = (value: unknown): string => {
  const normalized = coerceString(value)?.toUpperCase();
  if (!normalized) {
    return '—';
  }
  if (normalized === 'ISSUE_OUT') return 'Issue';
  if (normalized === 'TURN_IN') return 'Turn In';
  if (normalized === 'EXCHANGE') return 'DX';
  if (normalized === 'DISPOSAL') return 'Disposal';
  return toTitle(normalized);
};

const parseCatalogUnitCost = (
  entityData: Record<string, unknown> | null | undefined,
): number | null => {
  return parseNumber(entityData?.unit_price);
};

const parseAssetCondition = (
  entityData: Record<string, unknown> | null | undefined,
): string | null => {
  if (!entityData) return null;
  const candidates = [
    'condition_code',
    'conditionCode',
    'condition',
    'service_type',
    'serviceType',
  ];
  for (const key of candidates) {
    const value = coerceString(entityData[key]);
    if (value) return value;
  }
  return null;
};

const parsePersonOrganization = (
  entityData: Record<string, unknown> | null | undefined,
  metadata: Record<string, unknown> | null | undefined,
): string | null => {
  return (
    coerceString(entityData?.org) ??
    coerceString(entityData?.unit) ??
    coerceString(entityData?.organization) ??
    coerceString(metadata?.unit) ??
    coerceString(metadata?.organization)
  );
};

const parsePersonBillet = (
  entityData: Record<string, unknown> | null | undefined,
): string | null => {
  const rawBillet = entityData?.billet;
  if (!rawBillet || typeof rawBillet !== 'object') {
    return null;
  }

  const billet = rawBillet as Record<string, unknown>;
  return (
    coerceString(billet.positionTitle) ??
    coerceString(billet.dutyLocation) ??
    coerceString(billet.deploymentStatus) ??
    null
  );
};

const normalizeTransactionTypeFilter = (value: unknown): string | null => {
  const normalized = coerceString(value)?.toUpperCase();
  if (!normalized) return null;
  if (
    normalized === 'ISSUE_OUT' ||
    normalized === 'TURN_IN' ||
    normalized === 'EXCHANGE' ||
    normalized === 'DISPOSAL'
  ) {
    return normalized;
  }
  return null;
};

const normalizeIssueStatusFilter = (value: unknown): 'on_ie' | 'not_on_ie' | null => {
  const normalized = coerceString(value)?.toLowerCase().replace(/\s+/g, '_');
  if (normalized === 'on_ie') return 'on_ie';
  if (normalized === 'not_on_ie') return 'not_on_ie';
  return null;
};

const normalizeLin = (value: unknown): string | null => {
  const lin = coerceString(value);
  if (!lin || lin === '—') return null;
  return lin.toUpperCase();
};

const normalizeNsn = (value: unknown): string | null => {
  const nsn = coerceString(value);
  if (!nsn || nsn === '—') return null;
  return nsn;
};

const normalizeNomenclature = (value: unknown): string => {
  const nomenclature = coerceString(value);
  return nomenclature ?? '—';
};

const resolveBuyProgramUnitCost = (asset: TransactionLogAsset): number | null => {
  const actualPrice = parseNumber(asset.actualPrice);
  const tax = parseNumber(asset.tax);
  const shipping = parseNumber(asset.shipping);

  if (actualPrice == null && tax == null && shipping == null) {
    return null;
  }

  return (actualPrice ?? 0) + (tax ?? 0) + (shipping ?? 0);
};

const resolveIssueStatus = (
  memberId: string,
  lin: string | null,
  ieLinAssignments: Set<string>,
  ieKitLabel = 'IE Kit',
): string | null => {
  if (!lin) return null;
  const key = `${memberId}|${lin.toUpperCase()}`;
  return ieLinAssignments.has(key) ? `On ${ieKitLabel}` : `Not on ${ieKitLabel}`;
};

const matchesOptionalFilter = (candidate: string | null, filterValue: string): boolean => {
  if (!filterValue) return true;
  if (!candidate) return false;
  return candidate.toLowerCase().includes(filterValue);
};

export const runCostOfBusinessReport = async (params: Record<string, unknown>): Promise<void> => {
  const ieKitLabel = (params.ieKitLabel as string | undefined) ?? 'IE Kit';
  const fromDate = normalizeDateOnly(params.fromDate);
  const toDate = normalizeDateOnly(params.toDate);
  const linFilterTokens = parseCsvList(params.linFilter);
  const organizationFilter = coerceString(params.organization)?.toLowerCase() ?? '';
  const billetFilter = coerceString(params.billet)?.toLowerCase() ?? '';
  const issueStatusFilter = normalizeIssueStatusFilter(params.issueStatus);
  const transactionTypeFilter = normalizeTransactionTypeFilter(params.transactionType);
  const isDisposal = transactionTypeFilter === 'DISPOSAL';
  const individualParam = (params.individual ?? params.serviceMember) as { id: string; displayName: string } | null;
  const individualId = coerceString(individualParam?.id);

  const query = new URLSearchParams({ limit: '1000' });
  if (fromDate) query.set('from', fromDate);
  if (toDate) query.set('to', toDate);
  if (individualId) query.set('memberId', individualId);
  if (transactionTypeFilter) query.set('types', transactionTypeFilter);

  const response = await httpClient.get<TransactionLogResponse>(
    `/api/v1/transactions/log?${query.toString()}&source=report`,
  );
  const transactions = response.transactions ?? [];

  const memberIds = Array.from(
    new Set(
      transactions
        .map((transaction) => coerceString(transaction.memberId))
        .filter((memberId): memberId is string => Boolean(memberId) && isUuid(memberId)),
    ),
  );

  const personProfilesByMemberId = new Map<string, PersonProfile>();
  for (const memberIdBatch of chunk(memberIds, 75)) {
    const idFilter =
      memberIdBatch.length === 1 ? `eq.${memberIdBatch[0]}` : `in.(${memberIdBatch.join(',')})`;

    const people = await entityService.list({
      type: 'person',
      limit: memberIdBatch.length,
      order: 'id.asc',
      filters: {
        id: idFilter,
        select: 'id,entity_data,metadata',
      },
    });

    for (const person of people) {
      const entityData = (person.entity_data ?? {}) as Record<string, unknown>;
      const metadata = (person.metadata ?? {}) as Record<string, unknown>;
      personProfilesByMemberId.set(person.id, {
        organization: parsePersonOrganization(entityData, metadata),
        billet: parsePersonBillet(entityData),
      });
    }
  }

  const transactionsByPersonFilters = transactions.filter((transaction) => {
    const memberId = coerceString(transaction.memberId);
    const profile = memberId ? personProfilesByMemberId.get(memberId) : null;

    if (!matchesOptionalFilter(profile?.organization ?? null, organizationFilter)) {
      return false;
    }
    if (!matchesOptionalFilter(profile?.billet ?? null, billetFilter)) {
      return false;
    }
    return true;
  });

  const transactionAssetIds = Array.from(
    new Set(
      transactionsByPersonFilters
        .flatMap((transaction) => transaction.assets ?? [])
        .map((asset) => coerceString(asset.assetId))
        .filter((assetId): assetId is string => Boolean(assetId)),
    ),
  );

  const assetCatalogIdByAssetId = new Map<string, string | null>();
  const assetConditionByAssetId = new Map<string, string | null>();
  const catalogIds = new Set<string>();

  for (const assetIdBatch of chunk(transactionAssetIds, 75)) {
    const idFilter =
      assetIdBatch.length === 1 ? `eq.${assetIdBatch[0]}` : `in.(${assetIdBatch.join(',')})`;
    const assets = await entityService.list({
      type: 'asset',
      limit: assetIdBatch.length,
      order: 'id.asc',
      filters: {
        id: idFilter,
        select: 'id,entity_data',
      },
    });

    for (const asset of assets) {
      const entityData = (asset.entity_data ?? {}) as Record<string, unknown>;
      const catalogId = coerceString(entityData.catalog_id);
      assetCatalogIdByAssetId.set(asset.id, catalogId);
      assetConditionByAssetId.set(asset.id, parseAssetCondition(entityData));
      if (catalogId) {
        catalogIds.add(catalogId);
      }
    }
  }

  const catalogUnitCostByCatalogId = new Map<string, number | null>();
  for (const catalogIdBatch of chunk(Array.from(catalogIds), 75)) {
    const idFilter =
      catalogIdBatch.length === 1 ? `eq.${catalogIdBatch[0]}` : `in.(${catalogIdBatch.join(',')})`;
    const catalogs = await entityService.list({
      type: 'catalog',
      limit: catalogIdBatch.length,
      order: 'id.asc',
      filters: {
        id: idFilter,
        select: 'id,entity_data',
      },
    });

    for (const catalog of catalogs) {
      catalogUnitCostByCatalogId.set(
        catalog.id,
        parseCatalogUnitCost((catalog.entity_data ?? {}) as Record<string, unknown>),
      );
    }
  }

  const ieLinAssignments = new Set<string>();
  const filteredMemberIds = Array.from(
    new Set(
      transactionsByPersonFilters
        .map((transaction) => coerceString(transaction.memberId))
        .filter((memberId): memberId is string => Boolean(memberId) && isUuid(memberId)),
    ),
  );

  for (const memberIdBatch of chunk(filteredMemberIds, 75)) {
    const personIdFilter =
      memberIdBatch.length === 1 ? `eq.${memberIdBatch[0]}` : `in.(${memberIdBatch.join(',')})`;
    const ieQuery = new URLSearchParams({
      person_id: personIdFilter,
      select: 'person_id,lin,authorized_qty',
      limit: String(Math.max(memberIdBatch.length * 100, 500)),
    });

    const rows = await httpClient.get<RawIeSummaryRow[]>(
      `/api/v1/ie_summary?${ieQuery.toString()}`,
    );
    for (const row of rows) {
      const memberId = coerceString(row.person_id);
      const lin = normalizeLin(row.lin);
      const authorizedQty = parseNumber(row.authorized_qty);

      if (!memberId || !lin || authorizedQty == null || authorizedQty <= 0) {
        continue;
      }
      ieLinAssignments.add(`${memberId}|${lin}`);
    }
  }

  const items: CostOfBusinessItem[] = [];

  transactionsByPersonFilters.forEach((transaction) => {
    const memberId = coerceString(transaction.memberId);
    const memberName = coerceString(transaction.memberName);
    const fallbackDocumentNumber = coerceString(transaction.documentNumber);
    const transactionTypeLabel = formatTransactionTypeLabel(transaction.transactionType);

    (transaction.assets ?? []).forEach((asset) => {
      const lin = normalizeLin(asset.lin);
      if (linFilterTokens.length > 0 && (!lin || !linFilterTokens.includes(lin))) {
        return;
      }

      const issueStatus = resolveIssueStatus(memberId ?? '', lin, ieLinAssignments, ieKitLabel);
      if (issueStatusFilter === 'on_ie' && issueStatus !== `On ${ieKitLabel}`) {
        return;
      }
      if (issueStatusFilter === 'not_on_ie' && issueStatus !== `Not on ${ieKitLabel}`) {
        return;
      }

      const quantityIssued =
        typeof asset.transQty === 'number' && Number.isFinite(asset.transQty) && asset.transQty > 0
          ? asset.transQty
          : 1;

      const assetId = coerceString(asset.assetId);
      const catalogId = assetId ? (assetCatalogIdByAssetId.get(assetId) ?? null) : null;
      const conditionCode = assetId ? (assetConditionByAssetId.get(assetId) ?? null) : null;

      const unitCost =
        resolveBuyProgramUnitCost(asset) ??
        (catalogId ? (catalogUnitCostByCatalogId.get(catalogId) ?? null) : null);

      const totalLineCost =
        typeof unitCost === 'number' && Number.isFinite(unitCost)
          ? unitCost * quantityIssued
          : null;

      // Prefer per-asset document number (bulk disposals assign individual numbers)
      const documentNumber = coerceString(asset.documentNumber) ?? fallbackDocumentNumber;

      items.push({
        individual: memberName,
        documentNumber,
        lin,
        nsn: normalizeNsn(asset.nsn),
        nomenclature: normalizeNomenclature(asset.nomenclature),
        transactionTypeLabel,
        conditionCode,
        issueStatus,
        quantityIssued,
        unitCost,
        totalLineCost,
      });
    });
  });

  const knownLineCosts = items
    .map((item) => item.totalLineCost)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  const totalCost =
    knownLineCosts.length > 0 ? knownLineCosts.reduce((sum, value) => sum + value, 0) : null;

  const options: CostOfBusinessTemplateOptions = {
    personLabels: getPersonLabels(params),
    generatedAt: new Date(),
    header: {
      fromDate,
      toDate,
      reportDate: new Date().toISOString(),
      totalCost,
    },
    items,
    isDisposal,
  };

  const html = buildCostOfBusinessReportHtml(options);
  outputReport(html, 'cost-of-business-report.html', getOutputFormat(params));
};
